// What actually happened to a carrier label when the operator cancelled
// it — and how loudly to say so.
//
// Every endpoint that voids a consignment (cancel-label, reopen, bulk
// cancel) now calls APC synchronously and returns the carrier's real
// answer, because the previous "queued for cancellation" toast was shown
// whether or not the void ever reached APC. On 2026-08-11 that told the
// dispatch bench 44 labels were cancelled while every one of them stayed
// live and manifested at 20:00.
//
// Three outcomes, three different things the operator must do:
//   confirmed → nothing, the consignment is void
//   failed    → the label is STILL LIVE. Chase APC for a credit, and
//               make sure the parcel doesn't go out on the van.
//   retrying  → we couldn't reach APC; it retries in the background,
//               but the operator should verify before the cutoff.

export type CarrierVoidFailure = {
    waybill: string | null;
    error: string | null;
    manifested?: boolean;
};

// Add-box consignments: when one order's boxes come off a SHARED
// consignment, APC renumbers the pieces left on it — so the orders that
// keep travelling get new tracking numbers and their printed labels are
// dead. Portal re-issues them automatically; the packer must reprint.
export type CarrierLabelReissue = {
    orderId: string;
    bookingId: number;
    from: string[];
    to: string[];
};

export type CarrierVoidResponse = {
    /** Display name of the carrier the voided booking belonged to
     *  ("APC", "DPD Poland"); null when the BE could not tell. */
    carrier?: string | null;
    /** 'api' — the carrier was asked to cancel; 'local' — the carrier has
     *  no cancel API (DPD), the label is void in Portal only. */
    carrier_void_mode?: "api" | "local" | null;
    carrier_cancels_queued?: string[];
    carrier_cancels_confirmed?: string[];
    carrier_cancels_failed?: CarrierVoidFailure[];
    carrier_cancels_retrying?: { waybill: string | null; error: string | null }[];
    carrier_labels_reissued?: CarrierLabelReissue[];
};

export type CarrierVoidSummary = {
    /** Carrier name for the toasts; null → caller substitutes a generic. */
    carrier: string | null;
    /** True when the label was voided in Portal only (no cancel API). */
    localVoid: boolean;
    confirmed: number;
    failed: CarrierVoidFailure[];
    retrying: { waybill: string | null; error: string | null }[];
    reissued: CarrierLabelReissue[];
    /** Anything the operator has to act on. */
    needsAttention: boolean;
};

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export const summariseCarrierVoids = (data: CarrierVoidResponse | null | undefined): CarrierVoidSummary => {
    const failed = asArray<CarrierVoidFailure>(data?.carrier_cancels_failed);
    const retrying = asArray<{ waybill: string | null; error: string | null }>(data?.carrier_cancels_retrying);
    const reissued = asArray<CarrierLabelReissue>(data?.carrier_labels_reissued);
    const carrier = typeof data?.carrier === "string" && data.carrier.trim() ? data.carrier.trim() : null;
    return {
        carrier,
        localVoid: data?.carrier_void_mode === "local",
        confirmed: asArray<string>(data?.carrier_cancels_confirmed).length,
        failed,
        retrying,
        reissued,
        needsAttention: failed.length > 0 || retrying.length > 0 || reissued.length > 0,
    };
};

// "JLI_0807_12 (…045861002 → …045861001)" per affected order.
export const carrierReissueDetail = (reissued: CarrierLabelReissue[]): string =>
    reissued
        .map((r) => `${r.orderId} (${r.from.join(",")} → ${r.to.join(",")})`)
        .join(" | ");

// One line naming the waybills and why, for the error toast. Kept short —
// the toast carries the instruction, this carries the evidence.
export const carrierVoidFailureDetail = (failures: CarrierVoidFailure[]): string =>
    failures
        .map((f) => [f.waybill, f.error].filter(Boolean).join(": "))
        .join(" | ");
